import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { maskPhone } from '../../../utility'
import useCheckPhoneDeliverability from '../../../hooks/useCheckPhoneDeliverability'
import { MASKED_PHONE_NUMBER_CHAR_COUNT } from '../../../constants'
import CircularProgress from '@material-ui/core/CircularProgress'

const useStyles = makeStyles((theme) => ({
    paymentFields: {
        display: 'flex',
        minWidth: '269px',
        padding: '8px 0px',
        fontSize: '1rem',
        minHeight: 46,
        outline: 0,
        borderRadius: '4px',
        border: 'none',
        background: 'white',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        '&:focused': {
            boxShadow:
                'rgba(50, 50, 93, 0.109804) 0 4px 6px, rgba(0, 0, 0, 0.0784314) 0 1px 3px',
            '-webkit-transition': 'all 150ms ease',
            transition: 'all 150ms ease',
        },
        '&:last-of-type': {
            '& label': {
                minWidth: '42px',
                [theme.breakpoints.up('sm')]: {
                    minWidth: '90px',
                },
            },
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: '555px',
        },
    },
    inputAndSpinner: {
        position: 'relative',
        width: '100%',
    },
    input: {
        display: 'block',
        color: '#222',
        width: '100%',
        height: '64px',
        border: '2px solid #8a8a8a',
        borderRadius: '8px',
        padding: '1.4rem 1.5rem 0.25rem',
        fontSize: '1rem',
        background: 'white',
        boxShadow:
            '0 1px 3px rgba(50,50,93,.14902), 0 1px 0 rgba(0,0,0,.0196078)',
        letterSpacing: '.025em',
        outlineWidth: 0,
        [theme.breakpoints.up('sm')]: {},
        '&[readonly]': {
            color: '#818181',
        },
        '&::placeholder': {
            color: '#757575',
            opacity: 0.5,
        },
    },
    label: {
        position: 'absolute',
        left: '25px',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        pointerEvents: 'none',
        // color will be set inline for dynamic behavior
    },
    labelFloating: {
        top: '18px',
        left: '25px',
        fontSize: '0.8em',
    },
    errorInput: (props) => ({
        borderColor: '#D74242',
        borderWidth: props && props.errorBorderWidth ? props.errorBorderWidth : 2,
        borderStyle: 'solid',
    }),
    error: (props) => ({
        color: '#D74242',
        fontSize: props && props.errorFontSize ? props.errorFontSize : '.75rem',
        fontWeight: 400,
        marginTop: 6,
        marginBottom: 0,
    }),
    circularProgress: {
        position: 'absolute',
        right: 15,
        top: 0,
        bottom: 0,
        margin: 'auto 0',
    },
}))

const PhoneNumber = ({
    setBillingDetails,
    billingDetails,
    errorPhone,
    setErrorPhone,
    validateOnType,
    checkingPhone,
    setCheckingPhone,
    phoneCheckSuccess,
    setPhoneCheckSuccess,
    clearErrors,
    setShowCardError,
    labelColor = '#BBB',
    labelColorFocused = '#BBB',
    errorBorderWidth = 2,
    errorFontSize = '.75rem',
}) => {
    const classes = useStyles({ errorBorderWidth, errorFontSize })
    const [isFocused, setIsFocused] = useState(false)

    const {
        mutateAsync: checkPhoneDeliverability,
        isLoading: validateAddressIsLoading,
    } = useCheckPhoneDeliverability()

    const phoneNumberInput = React.useRef()

    useEffect(() => {
        if (!validateOnType) {
            setPhoneCheckSuccess(true)
            return
        }
        if (billingDetails.phone.length >= MASKED_PHONE_NUMBER_CHAR_COUNT) {
            setCheckingPhone(true)
            setPhoneCheckSuccess(false)
            setErrorPhone(null)
            checkPhoneDeliverability({ phone: billingDetails.phone })
                .then((data) => {
                    if (
                        !data.can_send_sms ||
                        data?.line_status === 'inactive'
                    ) {
                        setErrorPhone(
                            'We are unable to send a text to this number. Landline, VoIP, and International numbers are not supported. Please enter a valid phone number.'
                        )
                    } else if (data.duplicate_users) {
                        setErrorPhone(
                            'Whoops! That phone number is already in use by another customer.'
                        )
                    } else {
                        setErrorPhone(null)
                        clearErrors()
                        setPhoneCheckSuccess(true)
                    }
                })
                .catch((error) => {
                    console.log('checkPhoneDeliverability error', error)
                    if (
                        error.response?.status === 404 ||
                        error.response?.status === 302
                    ) {
                        console.log(
                            'checkPhoneDeliverability error response status',
                            error.response?.status
                        )
                        // Following are temporary until 302's can be resolved. CON-6135
                        setErrorPhone(null)
                        setPhoneCheckSuccess(true)
                        return
                    } else {
                        setErrorPhone(
                            'There was a problem checking this phone number. Please contact support.'
                        )
                        setPhoneCheckSuccess(false)
                    }
                })
                .finally(() => {
                    setCheckingPhone(false)
                })
        } else {
            setPhoneCheckSuccess(false)
        }
        // eslint-disable-next-line
    }, [billingDetails.phone])

    return (
        <fieldset className={classes.paymentFields}>
            <span style={{ width: '100%' }}>
                <div className={classes.inputAndSpinner}>
                    <label
                        className={`${classes.label} ${
                            billingDetails.phone || isFocused
                                ? classes.labelFloating
                                : ''
                        }`}
                            style={{
                                color: (billingDetails.phone || isFocused)
                                    ? (typeof labelColorFocused === 'string' ? labelColorFocused : '#BBB')
                                    : (typeof labelColor === 'string' ? labelColor : '#222')
                            }}
                    >
                        Mobile Phone Number
                    </label>
                    {checkingPhone && (
                        <CircularProgress
                            size={15}
                            className={classes.circularProgress}
                        />
                    )}
                    <input
                        className={`${classes.input} ${
                            errorPhone ? classes.errorInput : ''
                        }`}
                        type="tel"
                        required
                        ref={phoneNumberInput}
                        value={billingDetails.phone}
                        onChange={(e) => {
                            const phone = maskPhone(e.target.value)
                            setBillingDetails({
                                ...billingDetails,
                                phone,
                            })
                        }}
                        onFocus={() => {
                            setIsFocused(true)
                            setShowCardError(false)
                        }}
                        onBlurCapture={() => {
                            setIsFocused(false)
                        }}
                    />
                </div>
                {errorPhone && (
                    <p className={`${classes.error} phoneDeliverabilityError`}>
                        {errorPhone}
                    </p>
                )}
            </span>
        </fieldset>
    )
}

export default PhoneNumber
